import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TrackCtx = createContext(null);
export const useTracks = () => useContext(TrackCtx);

export function TrackProvider({ children }) {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTracks = useCallback(async () => {
    if (!user) {
      setTracks([]);
      setActiveTrackId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/career-tracks");
      const fetchedTracks = res.data.tracks || [];
      setTracks(fetchedTracks);

      const serverActiveId = res.data.active_track_id;
      const actAsMode = localStorage.getItem("ezjob_act_as") || "self";
      const userKey = `ezjob_active_track_id_${user.id}_${actAsMode}`;
      const localId = localStorage.getItem(userKey);

      let resolvedId = null;
      if (fetchedTracks.length === 0) {
        resolvedId = null;
      } else if (localId && fetchedTracks.some((t) => String(t.id) === String(localId))) {
        resolvedId = String(localId);
      } else if (serverActiveId && fetchedTracks.some((t) => String(t.id) === String(serverActiveId))) {
        resolvedId = String(serverActiveId);
      } else if (fetchedTracks.length > 0) {
        resolvedId = String(fetchedTracks[0].id);
      }

      if (resolvedId) {
        setActiveTrackId(resolvedId);
        localStorage.setItem(userKey, resolvedId);
        localStorage.setItem("ezjob_active_track_id", resolvedId);
      } else {
        setActiveTrackId(null);
        localStorage.removeItem(userKey);
        localStorage.removeItem("ezjob_active_track_id");
      }
    } catch (err) {
      console.warn("Could not load career tracks:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  const selectTrack = useCallback(async (id) => {
    const trackStrId = String(id);
    setActiveTrackId(trackStrId);
    if (user?.id) {
      const actAsMode = localStorage.getItem("ezjob_act_as") || "self";
      localStorage.setItem(`ezjob_active_track_id_${user.id}_${actAsMode}`, trackStrId);
    }
    localStorage.setItem("ezjob_active_track_id", trackStrId);
    window.dispatchEvent(new CustomEvent("ezjob:track-changed", { detail: { trackId: trackStrId } }));

    try {
      await api.post(`/career-tracks/select/${trackStrId}`);
    } catch (err) {
      console.warn("Failed to persist selected track to backend:", err);
    }
  }, [user]);

  const createTrack = useCallback(async (payload) => {
    try {
      const res = await api.post("/career-tracks", payload);
      const newTrack = res.data.track;
      const newActiveId = res.data.active_track_id || String(newTrack.id);

      setTracks((prev) => [...prev, newTrack]);
      selectTrack(newActiveId);
      toast.success(`Career track "${newTrack.name}" created!`);
      return newTrack;
    } catch (err) {
      toast.error(apiErr(err));
      throw err;
    }
  }, [selectTrack]);

  const updateTrack = useCallback(async (id, payload) => {
    try {
      const res = await api.put(`/career-tracks/${id}`, payload);
      const updated = res.data;
      setTracks((prev) => prev.map((t) => (String(t.id) === String(id) ? updated : t)));
      toast.success(`Career track "${updated.name}" updated!`);
      return updated;
    } catch (err) {
      toast.error(apiErr(err));
      throw err;
    }
  }, []);

  const deleteTrack = useCallback(async (id) => {
    try {
      const res = await api.delete(`/career-tracks/${id}`);
      const nextActiveId = res.data.active_track_id;
      setTracks((prev) => prev.filter((t) => String(t.id) !== String(id)));
      if (nextActiveId) {
        selectTrack(nextActiveId);
      }
      toast.success("Career track deleted.");
      return res.data;
    } catch (err) {
      toast.error(apiErr(err));
      throw err;
    }
  }, [selectTrack]);

  const activeTrack = tracks.find((t) => String(t.id) === String(activeTrackId)) || tracks[0] || null;

  return (
    <TrackCtx.Provider
      value={{
        tracks,
        activeTrack,
        activeTrackId,
        loading,
        selectTrack,
        createTrack,
        updateTrack,
        deleteTrack,
        refreshTracks: loadTracks,
      }}
    >
      {children}
    </TrackCtx.Provider>
  );
}
